import { FC, useState } from 'react';
import {
  IconButton,
  Menu,
  Box,
  Typography,
  Divider,
  Switch,
  Stack,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import Brightness4Icon from '@mui/icons-material/Brightness4';
import Brightness7Icon from '@mui/icons-material/Brightness7';
import PaletteIcon from '@mui/icons-material/Palette';
import TuneIcon from '@mui/icons-material/Tune';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useThemeContext, CUSTOM_THEMES } from 'providers/ThemeContext';

export const ThemeSettings: FC = () => {
  const { mode, toggleMode, customTheme, setCustomTheme } = useThemeContext();
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleThemeChange = (themeId: string) => {
    const selectedTheme = CUSTOM_THEMES.find((t) => t.id === themeId);
    if (selectedTheme) {
      setCustomTheme(selectedTheme);
    }
  };

  return (
    <>
      <IconButton
        onClick={handleClick}
        size="small"
        aria-label="تنظیمات ظاهری"
        sx={{
          width: 44,
          height: 44,
          borderRadius: 3,
          transition: theme.transitions.create(['background-color'], {
            duration: theme.transitions.duration.shortest,
          }),
          '&:hover': {
            backgroundColor: alpha(theme.palette.primary.main, 0.06),
          },
        }}
      >
        <TuneIcon sx={{ fontSize: 22 }} />
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        PaperProps={{
          sx: {
            width: 300,
            maxHeight: 440,
            borderRadius: 4,
            boxShadow:
              theme.palette.mode === 'dark'
                ? '0 16px 48px rgba(0,0,0,0.55), 0 4px 12px rgba(0,0,0,0.3)'
                : '0 16px 48px rgba(0,0,0,0.1), 0 4px 12px rgba(0,0,0,0.05)',
            border: `1px solid ${theme.palette.divider}`,
            mt: 1,
          },
        }}
        transformOrigin={{ horizontal: 'left', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'left', vertical: 'bottom' }}
      >
        <Box sx={{ px: 3, py: 2 }}>
          <Typography variant="subtitle2" fontWeight={700}>
            تنظیمات ظاهری
          </Typography>
          <Typography variant="caption" color="text.secondary">
            ظاهر اپلیکیشن را شخصی‌سازی کنید
          </Typography>
        </Box>
        <Divider />

        {/* Dark Mode Toggle */}
        <Box sx={{ px: 3, py: 2 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              p: 2,
              borderRadius: 3,
              backgroundColor: alpha(theme.palette.text.primary, 0.03),
              border: `1px solid ${theme.palette.divider}`,
            }}
          >
            <Box display="flex" alignItems="center" gap={1.5}>
              {mode === 'dark' ? (
                <Brightness4Icon sx={{ fontSize: 20, color: 'primary.main' }} />
              ) : (
                <Brightness7Icon sx={{ fontSize: 20, color: 'warning.main' }} />
              )}
              <Typography variant="body2" fontWeight={600}>
                {mode === 'dark' ? 'حالت تاریک' : 'حالت روشن'}
              </Typography>
            </Box>
            <Switch
              checked={mode === 'dark'}
              onChange={toggleMode}
              size="small"
            />
          </Box>
        </Box>

        <Divider />

        {/* Color Themes */}
        <Box sx={{ px: 3, py: 2 }}>
          <Box display="flex" alignItems="center" gap={1} mb={2}>
            <PaletteIcon fontSize="small" sx={{ color: 'text.secondary' }} />
            <Typography variant="body2" fontWeight={600}>
              رنگ‌بندی
            </Typography>
          </Box>
          <Stack spacing={1}>
            {CUSTOM_THEMES.map((t) => {
              const isSelected = customTheme.id === t.id;
              return (
                <Box
                  key={t.id}
                  onClick={() => {
                    handleThemeChange(t.id);
                    handleClose();
                  }}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    p: 1.5,
                    borderRadius: 3,
                    cursor: 'pointer',
                    border: `2px solid ${isSelected ? t.primary : 'transparent'}`,
                    backgroundColor: isSelected
                      ? alpha(t.primary, 0.06)
                      : 'transparent',
                    transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                    '&:hover': {
                      backgroundColor: alpha(t.primary, 0.08),
                    },
                  }}
                >
                  <Box display="flex" gap={0.5}>
                    <Box
                      sx={{
                        width: 32,
                        height: 32,
                        borderRadius: '50%',
                        background: `linear-gradient(135deg, ${t.primary}, ${alpha(t.primary, 0.7)})`,
                        boxShadow: `0 2px 8px ${alpha(t.primary, 0.3)}`,
                        border: isSelected
                          ? `2px solid ${t.primary}`
                          : '2px solid transparent',
                      }}
                    />
                    <Box
                      sx={{
                        width: 32,
                        height: 32,
                        borderRadius: '50%',
                        background: `linear-gradient(135deg, ${t.secondary}, ${alpha(t.secondary, 0.7)})`,
                        boxShadow: `0 2px 8px ${alpha(t.secondary, 0.3)}`,
                        mr: -1,
                      }}
                    />
                  </Box>
                  <Typography
                    variant="body2"
                    fontWeight={isSelected ? 700 : 500}
                    flex={1}
                  >
                    {t.name}
                  </Typography>
                  {isSelected && (
                    <CheckCircleIcon sx={{ fontSize: 20, color: t.primary }} />
                  )}
                </Box>
              );
            })}
          </Stack>
        </Box>
      </Menu>
    </>
  );
};
